import { useState, useMemo, useCallback } from 'react';
import { useGetAnnouncementByDealerIdQuery } from '../../../../../api';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const useDealerAnnouncementData = (dealerId) => {
  const { getCurrentLanguageId, th } = useLanguage();
  const languageId = typeof getCurrentLanguageId === 'function'
    ? getCurrentLanguageId()
    : getCurrentLanguageId;

  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().getFullYear());

  const queryParams = useMemo(() => {
    const params = {
      DealerId: dealerId,
      PageNumber: pagination.PageNumber,
      PageSize: pagination.PageSize,
      LanguageId: languageId,
      AnnouncementType: selectedType ?? null,
      Period: selectedPeriod ?? null
    };

    return params;
  }, [dealerId, pagination.PageNumber, pagination.PageSize, languageId, selectedType, selectedPeriod]);

  const {
    data: announcementData,
    isLoading,
    isFetching,
    error,
    refetch
  } = useGetAnnouncementByDealerIdQuery(queryParams, {
    skip: !dealerId
  });

  const announcements = useMemo(() => {
    const items = announcementData?.items ?? [];
    
    const typeMap = {
      128: th('announcement.typeLabels.sales'),
      129: th('announcement.typeLabels.marketing'),
      130: th('announcement.typeLabels.afterSales'),
      131: th('announcement.typeLabels.warranty'),
      132: th('announcement.typeLabels.spareParts')
    };
    
    return items.map(item => ({
      ...item,
      typeName: typeMap[item.type] || '-'
    }));
  }, [announcementData, th]);

  const totalCount = useMemo(() => {
    return announcementData?.totalCount ?? 0;
  }, [announcementData]);

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination({
      PageNumber: newPageNumber,
      PageSize: event.rows
    });
  }, []);

  const filterFields = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      years.push({ label: year.toString(), value: year });
    }

    return [
      {
        field: 'announcementType',
        headerName: th('announcement.placeholders.type'),
        filterPlaceholder: th('announcement.placeholders.type'),
        type: 'select',
        options: [
          { label: th('announcement.typeLabels.sales'), value: 128 },
          { label: th('announcement.typeLabels.marketing'), value: 129 },
          { label: th('announcement.typeLabels.afterSales'), value: 130 },
          { label: th('announcement.typeLabels.warranty'), value: 131 },
          { label: th('announcement.typeLabels.spareParts'), value: 132 }
        ]
      },
      {
        field: 'period',
        headerName: th('announcement.filterLabels.period'),
        type: 'select',
        options: years,
        defaultValue: currentYear
      }
    ];
  }, [th]);

  const customFilterTypes = useMemo(
    () => ({
      announcementType: 'select',
      period: 'select'
    }),
    []
  );

  const customFilterOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      years.push({ label: year.toString(), value: year });
    }

    return {
      announcementType: [
        { label: th('announcement.typeLabels.sales'), value: 128 },
        { label: th('announcement.typeLabels.marketing'), value: 129 },
        { label: th('announcement.typeLabels.afterSales'), value: 130 },
        { label: th('announcement.typeLabels.warranty'), value: 131 },
        { label: th('announcement.typeLabels.spareParts'), value: 132 }
      ],
      period: years
    };
  }, [th]);

 const handleApplyFilters = useCallback((filters) => {
  setSelectedType(filters.announcementType || null);
  setSelectedPeriod(filters.period || new Date().getFullYear());
  setPagination(prev => ({ ...prev, PageNumber: 1 }));
  refetch();
}, [refetch]);

  const handleResetFilters = useCallback(() => {
  setSelectedType(null);
  setSelectedPeriod(new Date().getFullYear());
  setPagination(prev => ({ ...prev, PageNumber: 1 }));
  refetch();
}, [refetch]);

  const handleSearch = useCallback((searchValue) => {
  }, []);

  return {
    announcements,
    loading: isLoading || isFetching,
    totalCount,
    pagination,
    selectedType,
    selectedPeriod,
    error,
    filterFields,
    customFilterTypes,
    customFilterOptions,
    onPageChange,
    handleApplyFilters,
    handleResetFilters,
    handleSearch,
    setSelectedType,
    setSelectedPeriod,
    refetch
  };
};

export default useDealerAnnouncementData;
